/**
 * Category tree helpers (dashboard).
 *
 * All operations are O(n) (plus one O(n log n) sibling sort):
 *  - normalizeCategories: flattens the API response (nested `children` or flat) iteratively,
 *    de-duplicates by id and keeps parent_id.                                      O(n)
 *  - buildCategoryTree: one Map(id -> node) pass + one linking pass, then an iterative DFS
 *    from the roots that sets depth and detects cycles; orphans (missing parent) and nodes
 *    trapped in a cycle are attached to the root level.                            O(n)
 *  - filterCategoryTree: marks matches and walks up their ancestors, stopping at an already
 *    marked ancestor, so every node is visited a constant number of times.         O(n)
 *  - collectDescendantIds: iterative DFS of one subtree.                           O(subtree)
 */

const toKey = (id) => (id === null || id === undefined || id === '' || id === 'null') ? null : String(id)

export const normalizeCategories = (input) => {
    const list = []
    const seen = new Map()
    const stack = []
    const push = (items, parentKey) => {
        if (!items) return
        const arr = Array.isArray(items) ? items : (typeof items === 'object' ? Object.values(items) : [])
        for (let i = arr.length - 1; i >= 0; i--) stack.push([arr[i], parentKey])
    }
    push(input, null)
    while (stack.length) {
        const [item, parentKey] = stack.pop()
        if (!item || typeof item !== 'object' || item.id === undefined || item.id === null) continue
        const key = toKey(item.id)
        if (!seen.has(key)) {
            const ownParent = toKey(item.parent_id)
            const record = {...item, parent_id: ownParent !== null ? item.parent_id : (parentKey !== null ? parentKey : null)}
            delete record.children
            seen.set(key, record)
            list.push(record)
        }
        if (item.children) push(item.children, key)
    }
    return list
}

const compareNodes = (a, b) => {
    const oa = Number(a.order) || 0
    const ob = Number(b.order) || 0
    if (oa !== ob) return oa - ob
    return String(a.name || '').localeCompare(String(b.name || ''), 'fa')
}

export const buildCategoryTree = (records) => {
    const byId = new Map()
    const roots = []
    for (let i = 0; i < records.length; i++) {
        const record = records[i]
        byId.set(toKey(record.id), {key: toKey(record.id), data: record, parent: null, children: [], depth: 0})
    }
    byId.forEach(node => {
        const parentKey = toKey(node.data.parent_id)
        const parent = parentKey !== null && parentKey !== node.key ? byId.get(parentKey) : null
        if (parent) {
            node.parent = parent
            parent.children.push(node)
        } else {
            roots.push(node) // root or orphan
        }
    })

    // iterative DFS from roots: depth + reachability (nodes not reached are in a cycle)
    const visited = new Set()
    const walk = (startNodes) => {
        const stack = []
        for (let i = startNodes.length - 1; i >= 0; i--) stack.push(startNodes[i])
        while (stack.length) {
            const node = stack.pop()
            if (visited.has(node.key)) continue
            visited.add(node.key)
            node.depth = node.parent ? node.parent.depth + 1 : 0
            for (let i = node.children.length - 1; i >= 0; i--) stack.push(node.children[i])
        }
    }
    walk(roots)
    if (visited.size !== byId.size) {
        byId.forEach(node => {
            if (visited.has(node.key)) return
            // break the cycle: detach from its parent and promote to root
            if (node.parent) {
                const siblings = node.parent.children
                const index = siblings.indexOf(node)
                if (index > -1) siblings.splice(index, 1)
                node.parent = null
            }
            roots.push(node)
            walk([node])
        })
    }

    roots.sort((a, b) => compareNodes(a.data, b.data))
    const stack = [...roots]
    while (stack.length) {
        const node = stack.pop()
        if (node.children.length > 1) node.children.sort((a, b) => compareNodes(a.data, b.data))
        for (let i = 0; i < node.children.length; i++) stack.push(node.children[i])
    }

    return {roots, byId, size: byId.size}
}

/**
 * Returns null when there is no query, otherwise {visible:Set, matches:Set}.
 * Ancestors of matches are visible (and should be shown expanded).
 */
export const filterCategoryTree = (tree, query) => {
    const q = String(query || '').trim().toLowerCase()
    if (!q) return null
    const visible = new Set()
    const matches = new Set()
    tree.byId.forEach(node => {
        const name = String(node.data.name || '').toLowerCase()
        const slug = String(node.data.slug || '').toLowerCase()
        if (name.indexOf(q) === -1 && slug.indexOf(q) === -1) return
        matches.add(node.key)
        let current = node
        while (current && !visible.has(current.key)) {
            visible.add(current.key)
            current = current.parent
        }
    })
    return {visible, matches}
}

export const collectDescendantIds = (tree, id) => {
    const result = new Set()
    const start = tree.byId.get(toKey(id))
    if (!start) return result
    const stack = [start]
    while (stack.length) {
        const node = stack.pop()
        if (result.has(node.key)) continue
        result.add(node.key)
        for (let i = 0; i < node.children.length; i++) stack.push(node.children[i])
    }
    return result
}

/**
 * Flattened visible rows in pre-order (parent always directly above its children).
 * Default: everything expanded. `isCollapsed(key)` hides only that node's children;
 * with a search filter only matches + their ancestors are listed (always expanded).
 * Each row carries tree-guide info for the connector lines:
 *   rails[i]  -> draw a continuing vertical line at ancestor level i+1
 *   isLast    -> node is the last visible child of its parent (└ instead of ├)
 * Visible children are counted per parent once, so cost is O(visible rows)
 * (+ O(depth) for each parent's rails array, shared by all of its children).
 */
export const visibleCategoryRows = (tree, {isCollapsed, isExpanded, filter, exclude, roots} = {}) => {
    const rows = []
    const keep = (node) => !(exclude && exclude.has(node.key)) && !(filter && !filter.visible.has(node.key))
    const stack = []
    const pushChildren = (children, rails) => {
        let lastIndex = -1
        for (let i = children.length - 1; i >= 0; i--) {
            if (keep(children[i])) {
                lastIndex = i
                break
            }
        }
        for (let i = lastIndex; i >= 0; i--) {
            if (keep(children[i])) stack.push([children[i], rails, i === lastIndex])
        }
    }
    pushChildren(roots || tree.roots, []) // `roots`: only these top-level nodes (paginated mode)
    while (stack.length) {
        const [node, rails, isLast] = stack.pop()
        let expanded = true
        if (!filter) {
            if (isCollapsed) expanded = !isCollapsed(node.key)
            else if (isExpanded) expanded = !!isExpanded(node.key)
        }
        rows.push({node, rails, isLast, expanded, isMatch: filter ? filter.matches.has(node.key) : false})
        if (expanded && node.children.length) {
            pushChildren(node.children, node.parent ? rails.concat(!isLast) : rails)
        }
    }
    return rows
}

/**
 * Merge record lists by id (later lists win for defined fields).                 O(n)
 */
export const mergeCategoryRecords = (...lists) => {
    const byId = new Map()
    for (let l = 0; l < lists.length; l++) {
        const list = lists[l] || []
        for (let i = 0; i < list.length; i++) {
            const record = list[i]
            const key = toKey(record.id)
            const current = byId.get(key)
            if (!current) {
                byId.set(key, record)
                continue
            }
            const next = {...current}
            Object.keys(record).forEach(field => {
                if (record[field] !== undefined) next[field] = record[field]
            })
            byId.set(key, next)
        }
    }
    return Array.from(byId.values())
}

export const categoryPathNames = (node) => {
    const names = []
    let current = node ? node.parent : null
    while (current) {
        names.unshift(current.data.name)
        current = current.parent
    }
    return names
}

export const categoryKey = toKey
